import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Toaster } from '../components/ui/toaster';

const RefundPolicy = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      <Toaster />

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 bg-gradient-to-br from-orange-50 via-white to-red-50">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-5xl lg:text-7xl font-black mb-8">
            Refund and&nbsp;
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-red-600">
               Cancellation Policy
            </span>
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Last Updated: January 2025
          </p>
        </div>
      </section>

      {/* Policy Content */}
      <section className="py-24 px-6">
        <div className="max-w-4xl mx-auto">
          
          {/* Introduction */}
          <div className="mb-12">
            <h2 className="text-3xl font-black mb-6">1. Introduction</h2>
            <p className="text-lg text-gray-700 leading-relaxed">
              Thank you for choosing our platform. We are dedicated to providing a reliable and high-performing solution to support your B2B lead generation and campaign management needs. This Refund and Cancellation Policy outlines the terms under which subscription cancellations and refund requests may be processed.
            </p>
          </div>

          {/* Subscription Cancellation */}
          <div className="mb-12 p-8 bg-gradient-to-br from-orange-50 to-red-50 rounded-2xl border-2 border-orange-100">
            <h2 className="text-3xl font-black mb-6">2. Subscription Cancellation</h2>
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">2.1</h3>
                <p className="text-lg text-gray-700 leading-relaxed">
                  You may cancel your subscription at any time by either contacting our support team at{' '}
                  <a href="mailto:support@aalign.io" className="text-orange-600 hover:text-orange-700 font-semibold underline">
                    support@aalign.io
                  </a>{' '}
                  or by using the cancellation option available in your account settings.
                </p>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">2.2</h3>
                <p className="text-lg text-gray-700 leading-relaxed">
                  Subscribers to annual plans are eligible for a prorated refund only if the cancellation request is made within thirty (30) days from the original date of purchase. No refunds will be issued for early termination beyond this period.
                </p>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">2.3</h3>
                <p className="text-lg text-gray-700 leading-relaxed">
                  We reserve the right to decline refund requests in cases involving abuse of the platform, breach of our Terms of Service, or misuse of any features.
                </p>
              </div>
            </div>
          </div>

          {/* Refund Eligibility */}
          <div className="mb-12">
            <h2 className="text-3xl font-black mb-6">3. Refund Eligibility</h2>
            
            <div className="mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">3.1 Refunds may be considered under the following circumstances:</h3>
              <div className="space-y-4 ml-6">
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-orange-600 rounded-full mt-3 flex-shrink-0"></div>
                  <div>
                    <span className="font-bold text-gray-900">3.1.1</span>{' '}
                    <span className="text-lg text-gray-700">Duplicate payments or inadvertent overcharges.</span>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-orange-600 rounded-full mt-3 flex-shrink-0"></div>
                  <div>
                    <span className="font-bold text-gray-900">3.1.2</span>{' '}
                    <span className="text-lg text-gray-700">Significant service disruptions or unresolved technical issues attributable to our platform that result in a material loss of functionality.</span>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-orange-600 rounded-full mt-3 flex-shrink-0"></div>
                  <div>
                    <span className="font-bold text-gray-900">3.1.3</span>{' '}
                    <span className="text-lg text-gray-700">Failure of the platform to meet expressly agreed service-level commitments, provided reasonable efforts to rectify the issue have not been successful.</span>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">3.2 Refunds will not be granted under the following conditions:</h3>
              <div className="space-y-4 ml-6">
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-red-600 rounded-full mt-3 flex-shrink-0"></div>
                  <div>
                    <span className="font-bold text-gray-900">3.2.1</span>{' '}
                    <span className="text-lg text-gray-700">Dissatisfaction with campaign performance or results, as these are influenced by external variables beyond our control.</span>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-red-600 rounded-full mt-3 flex-shrink-0"></div>
                  <div>
                    <span className="font-bold text-gray-900">3.2.2</span>{' '}
                    <span className="text-lg text-gray-700">Non-use of the platform during the subscription period.</span>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-red-600 rounded-full mt-3 flex-shrink-0"></div>
                  <div>
                    <span className="font-bold text-gray-900">3.2.3</span>{' '}
                    <span className="text-lg text-gray-700">Violations of our Terms of Service.</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Refund Process */}
          <div className="mb-12 p-8 bg-white rounded-2xl border-2 border-gray-200">
            <h2 className="text-3xl font-black mb-6">4. Refund Process</h2>
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">4.1</h3>
                <p className="text-lg text-gray-700 leading-relaxed">
                  All refund requests must be submitted in writing via email to{' '}
                  <a href="mailto:support@aalign.io" className="text-orange-600 hover:text-orange-700 font-semibold underline">
                    support@aalign.io
                  </a>{' '}
                  within 7 business days from the date of payment, occurrence of the issue, or discovery of an overcharge.
                </p>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">4.2</h3>
                <p className="text-lg text-gray-700 leading-relaxed">
                  The request must include your subscription information, proof of payment, and a detailed explanation of the reason for the refund request.
                </p>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">4.3</h3>
                <p className="text-lg text-gray-700 leading-relaxed">
                  Each request will be reviewed individually, and we may require additional documentation to verify the claim.
                </p>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">4.4</h3>
                <p className="text-lg text-gray-700 leading-relaxed">
                  Upon approval, refunds will be processed within 30 to 45 business days and will be issued to the original payment method unless an alternative arrangement has been agreed upon.
                </p>
              </div>
            </div>
          </div>

          {/* Communication */}
          <div className="mb-12 p-8 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl border-2 border-blue-100">
            <h2 className="text-3xl font-black mb-6">5. Communication</h2>
            <p className="text-lg text-gray-700 leading-relaxed">
              For any questions regarding subscription cancellations or refund inquiries, please contact our support team at{' '}
              <a href="mailto:support@aalign.io" className="text-orange-600 hover:text-orange-700 font-semibold underline">
                support@aalign.io
              </a>
              . Our representatives are available Monday through Friday, between 9:00 AM and 6:00 PM (UK time).
            </p>
          </div>

          {/* Policy Updates */}
          <div className="mb-12">
            <h2 className="text-3xl font-black mb-6">6. Policy Updates</h2>
            <p className="text-lg text-gray-700 leading-relaxed">
              We may update this policy from time to time. All modifications will be effective upon posting to our official website at{' '}
              <a href="https://www.aalign.io" className="text-orange-600 hover:text-orange-700 font-semibold underline">
                www.aalign.io
              </a>
              . It is your responsibility to periodically review this policy.
            </p>
          </div>

          {/* Contact CTA */}
          <div className="mt-16 p-8 bg-gradient-to-r from-orange-500 to-red-600 rounded-2xl text-white text-center">
            <h3 className="text-3xl font-bold mb-4">Have Questions?</h3>
            <p className="text-lg mb-6 text-orange-100">
              Our support team is here to help with any refund or cancellation inquiries.
            </p>
            <a
              href="mailto:support@aalign.io"
              className="inline-block bg-white text-orange-600 hover:bg-gray-100 font-bold px-10 py-4 rounded-xl shadow-lg transition-all transform hover:scale-105"
            >
              Contact Support
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default RefundPolicy;
